import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, ClipboardList, FileText, Loader2, ScanText, Sparkles, Wand2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { track } from "@/lib/meta";

const field = "bg-zinc-900/70 border-zinc-800 text-sm";

const STEPS = [
  { key: "client", title: "Who is this for?", hint: "The client and their world" },
  { key: "objective", title: "What outcome do they want?", hint: "The business result on the line" },
  { key: "scope", title: "What are you delivering?", hint: "Scope and deliverables" },
  { key: "pricing", title: "How is it priced?", hint: "Budget signals and structure" },
  { key: "timeline", title: "How long will it take?", hint: "Phases and milestones" },
  { key: "terms", title: "Terms and tone", hint: "Payment terms and voice" },
];

const GEN_STAGES = ["Reading your brief", "Structuring sections", "Writing copy", "Pricing & terms", "Fact-checking claims"];

export default function AIStudio() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("wizard");
  const [step, setStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [extraction, setExtraction] = useState(null);
  const [form, setForm] = useState({
    client_id: "",
    client_name: "",
    client_industry: "",
    objective: "",
    scope: "",
    deliverables: "",
    pricing_notes: "",
    timeline: "",
    terms_notes: "",
    tone: "confident",
    currency: "INR",
    extra_context: "",
  });

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  useEffect(() => {
    if (!busy) return setStage(0);
    const t = setInterval(() => setStage((s) => (s + 1) % GEN_STAGES.length), 2200);
    return () => clearInterval(t);
  }, [busy]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/wizard", form);
      track("AIProposalGenerated", { source: "wizard", content_ids: [data.id] });
      track("ProposalCreated", { source: "ai_wizard", content_ids: [data.id] });
      toast.success("Proposal drafted");
      navigate(`/editor/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doExtract = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/transcript/extract", { transcript });
      setExtraction(data);
      toast.success("Extracted — review before generating");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const generateFromTranscript = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/transcript/generate", {
        extraction,
        client_id: form.client_id || null,
        currency: form.currency,
      });
      track("AIProposalGenerated", { source: "transcript", content_ids: [data.id] });
      track("ProposalCreated", { source: "ai_transcript", content_ids: [data.id] });
      toast.success("Proposal drafted from meeting");
      navigate(`/editor/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const renderStep = () => {
    if (step === 0)
      return (
        <div className="space-y-3">
          <select
            data-testid="wizard-client-select"
            className={`h-10 w-full rounded-md px-2 ${field}`}
            value={form.client_id}
            onChange={(e) => {
              const c = clients.find((x) => x.id === e.target.value);
              setForm({
                ...form,
                client_id: e.target.value,
                client_name: c?.name || form.client_name,
                client_industry: c?.industry || form.client_industry,
                currency: c?.currency || form.currency,
              });
            }}
          >
            <option value="">Not in my client list yet</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <Input data-testid="wizard-client-name-input" className={field} placeholder="Client / company name" value={form.client_name} onChange={set("client_name")} />
          <Input data-testid="wizard-industry-input" className={field} placeholder="Industry (e.g. D2C skincare)" value={form.client_industry} onChange={set("client_industry")} />
          <select data-testid="wizard-currency-select" className={`h-10 w-full rounded-md px-2 ${field}`} value={form.currency} onChange={set("currency")}>
            {["INR", "USD", "EUR", "GBP", "AED", "SGD", "AUD"].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      );
    if (step === 1)
      return <Textarea data-testid="wizard-objective-input" className={`${field} min-h-[140px]`} placeholder="What are they trying to achieve, and what happens if they don't?" value={form.objective} onChange={set("objective")} />;
    if (step === 2)
      return (
        <div className="space-y-3">
          <Textarea data-testid="wizard-scope-input" className={`${field} min-h-[110px]`} placeholder="Scope of work" value={form.scope} onChange={set("scope")} />
          <Textarea data-testid="wizard-deliverables-input" className={`${field} min-h-[90px]`} placeholder="Concrete deliverables, one per line" value={form.deliverables} onChange={set("deliverables")} />
        </div>
      );
    if (step === 3)
      return <Textarea data-testid="wizard-pricing-input" className={`${field} min-h-[140px]`} placeholder="Budget signals, retainer vs project, tiers you want offered" value={form.pricing_notes} onChange={set("pricing_notes")} />;
    if (step === 4)
      return <Textarea data-testid="wizard-timeline-input" className={`${field} min-h-[140px]`} placeholder="Start date, phases, key milestones" value={form.timeline} onChange={set("timeline")} />;
    return (
      <div className="space-y-3">
        <Textarea data-testid="wizard-terms-input" className={`${field} min-h-[100px]`} placeholder="Payment terms, notice period, exclusions" value={form.terms_notes} onChange={set("terms_notes")} />
        <select data-testid="wizard-tone-select" className={`h-10 w-full rounded-md px-2 ${field}`} value={form.tone} onChange={set("tone")}>
          {["confident", "warm", "formal", "direct", "consultative"].map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <Textarea data-testid="wizard-extra-input" className={field} placeholder="Anything else the AI must know (facts only)" value={form.extra_context} onChange={set("extra_context")} />
      </div>
    );
  };

  return (
    <AppShell title="AI Studio" subtitle="Draft a full proposal from a brief or a meeting transcript">
      <div className="inline-flex gap-1 mb-6 p-1 rounded-xl border border-zinc-800 bg-zinc-900/60 backdrop-blur">
        {[
          ["wizard", "Guided wizard", Wand2],
          ["transcript", "From transcript", FileText],
        ].map(([k, label, Icon]) => (
          <button
            key={k}
            data-testid={`ai-mode-${k}-btn`}
            onClick={() => setMode(k)}
            className={`relative inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-medium transition-all duration-200 ${
              mode === k
                ? "bg-indigo-500 text-white shadow-[0_6px_20px_-8px_rgba(99,102,241,0.9)]"
                : "text-zinc-400 hover:text-zinc-100 hover:bg-white/[0.04]"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {busy && (
        <div data-testid="generation-progress" className="nv-card p-5 mb-5 relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-0.5 bg-zinc-800">
            <div
              className="h-full bg-indigo-500 transition-all duration-700"
              style={{ width: `${Math.round(((stage + 1) / GEN_STAGES.length) * 100)}%` }}
            />
          </div>
          <div className="flex items-center gap-2 mb-3">
            <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
            <p className="text-sm text-zinc-200">{GEN_STAGES[stage]}…</p>
            <span className="ml-auto text-xs font-num text-zinc-500">
              {Math.round(((stage + 1) / GEN_STAGES.length) * 100)}%
            </span>
          </div>
          <div className="space-y-1.5">
            {GEN_STAGES.map((s, i) => (
              <div key={s} className="flex items-center gap-2 text-xs">
                {i < stage ? (
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                ) : (
                  <span className={`w-3.5 h-3.5 rounded-full border ${i === stage ? "border-indigo-400 animate-pulse" : "border-zinc-700"}`} />
                )}
                <span className={i <= stage ? "text-zinc-300" : "text-zinc-600"}>{s}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === "wizard" ? (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 nv-card p-6 sm:p-7">
            <ol className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
              {STEPS.map((s, i) => (
                <li key={s.key} className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => i < step && setStep(i)}
                    disabled={i > step}
                    data-testid={`wizard-step-dot-${i}`}
                    title={s.title}
                    className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-num font-semibold transition-all duration-300 ${
                      i < step
                        ? "bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/40 cursor-pointer hover:bg-emerald-500/25"
                        : i === step
                          ? "bg-indigo-500 text-white shadow-[0_0_0_4px_rgba(99,102,241,0.18)]"
                          : "bg-zinc-900 text-zinc-600 ring-1 ring-zinc-800"
                    }`}
                  >
                    {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </button>
                  {i < STEPS.length - 1 && (
                    <span className={`h-px w-6 sm:w-10 transition-colors duration-300 ${i < step ? "bg-emerald-500/50" : "bg-zinc-800"}`} />
                  )}
                </li>
              ))}
            </ol>
            <p className="text-[10px] font-num uppercase tracking-[0.2em] text-indigo-400">
              Step {step + 1} of {STEPS.length}
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1.5">{STEPS[step].title}</h2>
            <p className="text-sm text-zinc-500 mb-6">{STEPS[step].hint}</p>

            <motion.div
              key={step}
              initial={{ opacity: 0, x: 14 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              {renderStep()}
            </motion.div>

            <div className="flex items-center gap-2 mt-7 pt-5 border-t border-zinc-800/80">
              <Button data-testid="wizard-back-btn" variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)} className="border-zinc-800 text-zinc-300">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
              </Button>
              {step < STEPS.length - 1 ? (
                <Button data-testid={`ai-guided-wizard-step-${step + 1}-next`} onClick={() => setStep((s) => s + 1)} className="bg-indigo-500 hover:bg-indigo-600 text-white shadow-[0_8px_24px_-10px_rgba(99,102,241,0.9)]">
                  Next <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              ) : (
                <Button data-testid="wizard-generate-btn" onClick={generate} disabled={busy} className="bg-indigo-500 hover:bg-indigo-600 text-white shadow-[0_8px_24px_-10px_rgba(99,102,241,0.9)]">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-1.5" /> Generate proposal</>}
                </Button>
              )}
              <span className="ml-auto text-xs text-zinc-600 font-num hidden sm:block">
                {STEPS.length - step - 1} step{STEPS.length - step - 1 === 1 ? "" : "s"} left
              </span>
            </div>
          </div>

          <div className="nv-card p-5 h-fit lg:sticky lg:top-24">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-lg bg-indigo-500/15 grid place-items-center">
                <Sparkles className="w-4 h-4 text-indigo-400" />
              </span>
              <p className="text-sm font-semibold">How this works</p>
            </div>
            <ul className="mt-4 space-y-3 text-xs text-zinc-500">
              {[
                <>We only use facts you provide. Anything unverifiable becomes a <span className="text-amber-400 font-num">[CONFIRM]</span> flag.</>,
                <>You get a full block-based draft: cover, scope, pricing, timeline, terms and signature.</>,
                <>Every section can be rewritten, shortened, expanded or translated with one click.</>,
                <>The fact-check guard blocks sending until flagged claims are verified.</>,
              ].map((copy, i) => (
                <li key={i} className="flex gap-2.5">
                  <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{copy}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="nv-card p-6 sm:p-7">
            <div className="flex items-center gap-2.5 mb-4">
              <span className="w-8 h-8 rounded-lg bg-indigo-500/15 grid place-items-center shrink-0">
                <ScanText className="w-4 h-4 text-indigo-400" />
              </span>
              <div>
                <p className="text-[10px] font-num uppercase tracking-[0.2em] text-indigo-400">Step 1 of 2</p>
                <h2 className="text-xl font-bold tracking-tight">Paste your meeting transcript</h2>
              </div>
            </div>
            <p className="text-sm text-zinc-500 mb-4">Fathom, Otter, Zoom, Granola or plain notes all work.</p>
            <Textarea
              data-testid="transcript-input"
              className={`${field} min-h-[320px] font-mono text-xs`}
              placeholder="Paste the transcript here…"
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
            />
            <div className="flex items-center gap-3 mt-3">
              <Button data-testid="extract-transcript-btn" onClick={doExtract} disabled={busy || transcript.length < 50} className="bg-indigo-500 hover:bg-indigo-600 text-white shadow-[0_8px_24px_-10px_rgba(99,102,241,0.9)]">
                {busy && !extraction ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ScanText className="w-4 h-4 mr-1.5" /> Extract details</>}
              </Button>
              <span className="text-xs font-num text-zinc-600">
                {transcript.length < 50 ? `${50 - transcript.length} more characters needed` : `${transcript.length.toLocaleString()} characters ready`}
              </span>
            </div>
          </div>

          <div className="nv-card p-6 sm:p-7">
            <div className="flex items-center gap-2.5 mb-4">
              <span className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${extraction ? "bg-emerald-500/15" : "bg-zinc-800/80"}`}>
                <ClipboardList className={`w-4 h-4 ${extraction ? "text-emerald-400" : "text-zinc-500"}`} />
              </span>
              <div>
                <p className={`text-[10px] font-num uppercase tracking-[0.2em] ${extraction ? "text-emerald-400" : "text-zinc-600"}`}>Step 2 of 2</p>
                <h2 className="text-xl font-bold tracking-tight">Review extraction</h2>
              </div>
            </div>
            <p className="text-sm text-zinc-500 mb-4">Fix anything wrong before we write the proposal.</p>
            {!extraction ? (
              <div className="py-14 text-center">
                <span className="w-11 h-11 rounded-full bg-zinc-900 border border-zinc-800 grid place-items-center mx-auto">
                  <FileText className="w-5 h-5 text-zinc-600" />
                </span>
                <p className="mt-3 text-sm font-medium text-zinc-400">Nothing extracted yet</p>
                <p className="mt-1 text-xs text-zinc-600">Paste a transcript on the left and hit Extract details.</p>
              </div>
            ) : (
              <div data-testid="extraction-review" className="space-y-3 max-h-[380px] overflow-y-auto nv-scroll pr-1">
                {Object.entries(extraction).map(([k, v]) => (
                  <div key={k}>
                    <label className="text-[10px] font-num uppercase tracking-[0.16em] text-zinc-500">{k.replace(/_/g, " ")}</label>
                    {Array.isArray(v) ? (
                      <Textarea
                        data-testid={`extract-${k}`}
                        className={`mt-1 ${field}`}
                        value={v.join("\n")}
                        onChange={(e) => setExtraction({ ...extraction, [k]: e.target.value.split("\n") })}
                      />
                    ) : (
                      <Input data-testid={`extract-${k}`} className={`mt-1 ${field}`} value={v || ""} onChange={(e) => setExtraction({ ...extraction, [k]: e.target.value })} />
                    )}
                  </div>
                ))}
                <Button data-testid="generate-from-transcript-btn" onClick={generateFromTranscript} disabled={busy} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-1.5" /> Generate proposal</>}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}
